import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import ShotTarget from '../tv/shotTarget';
import { bulletMm, formatShotValue } from '../../lib/pistolTargets';
import { formatSeconds } from '../../lib/shooterShots';
import { getStageTypeDisplay } from '../../lib/conventionResults';
import { buildShotHistory, PERIOD_DAYS } from '../../lib/shotHistory';
import ShotAnalysis, {
    ProgramAnalysis, seriesOverlays, seriesHeat, ANALYSIS_CARDS, CARD_TITLES, enabledAnalyses, settingKey,
} from './shotAnalysis';

const SERIES_TARGET_PX = 150;
const LAG_TARGET_PX = 84;

/**
 * The «Skudd» tab tile on the profile page. Lives in the product registry
 * (components-other) with ShotHistory so the Dyrejournal build never carries
 * the Megalink code. profile.js renders it only when the shooter has shots.
 */
export function ShotHistoryProfileTile({ view, darkmode, onToggle }) {
    const active = view === 'shots';
    const tone = darkmode ? 'bg-darkmode' : 'bg-lightmode';
    return (
        <div
            class={`d-flex flex-column align-items-center px-1 py-3 box-container ${active ? 'border border-primary bg-primary text-white' : tone}`}
            style='flex: 1 1 calc(25% - 0.5rem); min-width: 70px;'
            onClick={onToggle}
            data-view='shots'
        >
            <span style='font-size: 1.5rem;'>
                <i class='fa-duotone fa-bullseye' />
            </span>
            <small class='text-center font-style-narrow'><Text id='user.shots'>Shots</Text></small>
        </div>
    );
}

/**
 * The shooter's own Megalink shots, program by program: one target per series
 * type (150s, 5x3s, …) with every shot of that type in the chosen period and
 * the mean point of impact. Tapping a target opens the Lag-by-Lag history of
 * that series type underneath, with the group / pace / progress analysis
 * (components/user/shotAnalysis.js) above the Lag rows and the group ellipse
 * and CEP 95 circle drawn on the opened target. Which analyses show is the
 * shooter's choice («Analyser» switches), kept in user.settings through
 * userStore.updateSetting. Scores are secondary here — the point is where
 * the shots land.
 */
@observer
class ShotHistory extends Component {
    constructor(props) {
        super(props);
        this.state = { openKey: null, showToggles: false, chosen: {} };
    }

    // The shooter's choice: stored settings under the choices made in this
    // session (updateSetting answers after the PATCH; the switch flips at once).
    enabled() {
        const { userStore } = this.props.stores;
        const settings = userStore && userStore.user ? userStore.user.settings : null;
        return { ...enabledAnalyses(settings), ...this.state.chosen };
    }

    toggleToggles = (e) => {
        e.preventDefault();
        this.setState(prevState => ({ showToggles: !prevState.showToggles }));
    }

    toggleAnalysis = (key) => () => {
        const { userStore } = this.props.stores;
        const value = this.enabled()[key] ? 0 : 1;
        this.setState(prevState => ({ chosen: { ...prevState.chosen, [key]: value === 1 } }));
        if (userStore && typeof userStore.updateSetting === 'function') {
            userStore.updateSetting({ [`settings.${settingKey(key)}`]: value }, { [settingKey(key)]: value });
        }
    }

    componentDidMount() {
        const { megalinkShotStore } = this.props.stores;
        megalinkShotStore.loadMine();
    }

    setPeriod = (days) => (e) => {
        e.preventDefault();
        const { megalinkShotStore } = this.props.stores;
        megalinkShotStore.loadMine({ days });
    }

    toggleSeries = (key) => (e) => {
        e.preventDefault();
        this.setState(prevState => ({ openKey: prevState.openKey === key ? null : key }));
    }

    labelsFor = (type) => {
        const { conventionResultStore } = this.props.stores;
        if (!conventionResultStore || typeof conventionResultStore.getParticipantFields !== 'function') {
            return [];
        }
        return conventionResultStore.getParticipantFields({ type });
    }

    periodLabel(days) {
        if (days === 30) return <Text id='user.shotHistory-30'>30 days</Text>;
        if (days === 365) return <Text id='user.shotHistory-365'>365 days</Text>;
        return <Text id='user.shotHistory-all'>All</Text>;
    }

    renderLag(lag, program) {
        const date = lag.date ? util.formatDate(lag.date, { locale: 'nb-NO' }) : '';
        return (
            <div key={`${lag.conventionId}|${lag.stageName}|${lag.date}`} class='shot-history-lag d-flex align-items-center gap-2 py-2 border-bottom'>
                <div style={`flex: 0 0 ${LAG_TARGET_PX}px;`}>
                    <ShotTarget target={program.target} shots={lag.shots} bulletMm={bulletMm(program.type)} size={`${LAG_TARGET_PX}px`} numbered={false} />
                </div>
                <div class='flex-grow-1' style='min-width: 0;'>
                    <div class='text-body-secondary small'>{date}</div>
                    <div class='text-truncate'>
                        {lag.conventionId > 0 ? <a href={`/convention/${lag.conventionId}`}>{lag.title}</a> : lag.title}
                    </div>
                    <div class='small'>
                        {lag.stageName}
                        {lag.stageName ? ' · ' : ''}
                        <Text id='user.shotHistory-shots' fields={{ count: lag.count }}>{lag.count} shots</Text>
                    </div>
                    {(lag.groups || []).map(group => (
                        <div key={group.targetIndex} class='shot-history-group d-flex flex-wrap align-items-start gap-1 mt-1 small' style='font-variant-numeric: tabular-nums;'>
                            {group.shots.map((shot, i) => (
                                <span key={shot.shotNo || i} class='shot-cell d-inline-flex flex-column align-items-center' style='line-height: 1.1;'>
                                    <span class='shot-value rounded bg-body-secondary px-1'>{formatShotValue(shot)}</span>
                                    {Number.isInteger(shot.split) && (
                                        <small class='shot-split text-body-secondary' style='font-size: 0.65rem; white-space: nowrap;'>+{formatSeconds(shot.split)}</small>
                                    )}
                                </span>
                            ))}
                            {Number.isInteger(group.duration) && (
                                <span class='series-duration text-body-secondary align-self-center ms-1' style='white-space: nowrap;'>
                                    <i class='fa-duotone fa-stopwatch me-1' />{formatSeconds(group.duration)} s
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderToggles() {
        const on = this.enabled();
        return (
            <div class='shot-analysis-toggles d-flex flex-wrap gap-3 mb-2 p-2 rounded border small'>
                {ANALYSIS_CARDS.map(card => (
                    <div key={card.key} class='form-check form-switch mb-0'>
                        <input
                            class='form-check-input'
                            type='checkbox'
                            role='switch'
                            id={`shot-analysis-${card.key}`}
                            data-analysis={card.key}
                            checked={!!on[card.key]}
                            onChange={this.toggleAnalysis(card.key)}
                        />
                        <label class='form-check-label' for={`shot-analysis-${card.key}`}>{CARD_TITLES[card.key]}</label>
                    </div>
                ))}
            </div>
        );
    }

    renderSeries(series, type) {
        const { openKey } = this.state;
        const open = openKey === series.key;
        const heat = this.enabled().heatmap ? seriesHeat(series) : null;
        return (
            <a
                key={series.key}
                href='#'
                class={`shot-history-series text-decoration-none text-body d-flex flex-column align-items-center rounded border p-2 ${open ? 'border-primary' : ''}`}
                style={`flex: 0 0 auto; width: ${SERIES_TARGET_PX + 18}px;`}
                onClick={this.toggleSeries(series.key)}
            >
                <strong>{series.label}</strong>
                <ShotTarget target={series.target} shots={series.shots} bulletMm={bulletMm(type)} size={`${SERIES_TARGET_PX}px`} numbered={false} overlays={open ? seriesOverlays(series) : null} heat={heat} />
                <small class='text-body-secondary'>
                    <Text id='user.shotHistory-shots' fields={{ count: series.count }}>{series.count} shots</Text>
                    {' · '}
                    <Text id='user.shotHistory-lags' fields={{ count: series.lags.length }}>{series.lags.length} Lag</Text>
                </small>
            </a>
        );
    }

    renderProgram(program) {
        const { openKey } = this.state;
        const openSeries = program.series.find(s => s.key === openKey) || null;
        return (
            <div key={program.type} class='shot-history-program mb-4'>
                <h5 class='mb-2'>
                    {getStageTypeDisplay(program.type) || program.type}
                    <small class='text-body-secondary ms-2'>
                        <Text id='user.shotHistory-shots' fields={{ count: program.count }}>{program.count} shots</Text>
                    </small>
                </h5>
                <div class='d-flex flex-wrap gap-2'>
                    {program.series.map(series => this.renderSeries(series, program.type))}
                </div>
                {this.enabled().types && <ProgramAnalysis program={program} />}
                {openSeries && <ShotAnalysis series={openSeries} type={program.type} stores={this.props.stores} enabled={this.enabled()} />}
                {openSeries && (
                    <div class='shot-history-lags mt-2 px-2 rounded border'>
                        <div class='pt-2 small text-body-secondary'>
                            {getStageTypeDisplay(program.type) || program.type} · {openSeries.label}
                        </div>
                        {openSeries.lags.map(lag => this.renderLag(lag, { ...openSeries, type: program.type }))}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { megalinkShotStore } = this.props.stores;
        const { history, historyDays, historyLoading } = megalinkShotStore;
        const programs = buildShotHistory(history.lags, this.labelsFor);
        return (
            <div class='shot-history w-100'>
                <div class='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2'>
                    <div>
                        <h4 class='mb-0'><Text id='user.shotHistory-title'>My shot history</Text></h4>
                        <small class='text-body-secondary'>
                            <Text id='user.shotHistory-subtitle'>Megalink shots per series type. Tap a target to see it Lag by Lag.</Text>
                        </small>
                    </div>
                    <div class='d-flex gap-1'>
                        <button type='button' class={`btn btn-sm shot-analysis-toggle-button ${this.state.showToggles ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={this.toggleToggles} title='Analyser'>
                            <i class='fa-duotone fa-sliders' />
                        </button>
                        <div class='btn-group btn-group-sm' role='group'>
                            {PERIOD_DAYS.map(days => (
                                <button
                                    key={days}
                                    type='button'
                                    class={`btn ${historyDays === days ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={this.setPeriod(days)}
                                >
                                    {this.periodLabel(days)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {this.state.showToggles && this.renderToggles()}
                {historyLoading && (
                    <div class='text-body-secondary py-2'><i class='fa-duotone fa-spinner fa-spin me-1' /><Text id='user.shotHistory-loading'>Loading…</Text></div>
                )}
                {!historyLoading && programs.length === 0 && (
                    <div class='alert alert-secondary'>
                        <Text id='user.shotHistory-empty'>No Megalink shots in this period.</Text>
                    </div>
                )}
                {programs.map(program => this.renderProgram(program))}
            </div>
        );
    }
}

export default ShotHistory;
